import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import Stack from "../../../components/Stack";
import React, { useEffect, useState } from "react";
import BusIcon from "../../../components/icons/BusIcon";
import DropOffIcon from "../../../components/icons/DropOffIcon";
import { useDataProvider, useNotify } from "react-admin";
import moment from "moment/moment";
import { calculateBusPosition } from "../tools";
import MapLocationDotIcon from "../../../components/icons/MapLocationDotIcon";
import {
  getBusAssistantsByIds,
  getDriversByIds,
  getVehiclesByIds,
} from "../../enhancedRoutePlans/tools";
import Tooltip from "@material-ui/core/Tooltip";

interface RouteDetailProps {
  selectedRoute: IAssignmentWithVehicleLocation;
  assignmentVehicleLocations: IAssignmentWithVehicleLocation[];
  handleCloseAssignmentDetail: () => void;
  setSelectedAssignment: (assignment: IAssignment | null) => void;
}

const useStyles = makeStyles(() => ({
  routeDetails: {
    position: "absolute",
    top: "40px",
    left: "40px",
    width: "320px",
    borderRadius: "10px",
    padding: "14px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
    zIndex: 1000,
    visibility: "hidden", // Initially hidden
    maxHeight: "90%",
  },
  visibleRouteDetails: {
    visibility: "visible", // Shown when route is selected
  },
  assignmentTime: {
    color: "#2B3674",
    fontSize: "12px",
    fontWeight: 500,
  },
  icon: {
    position: "absolute",
    top: "5px",
    left: "-14px",
  },
  iconForFirstLocation: {
    position: "absolute",
    top: "0px",
    left: "-14px",
  },
  busIcon: {
    position: "absolute",
    left: "-13px",
    "& svg": {
      width: "24px",
      height: "24px",
    },
    transition: "top 0.5s ease",
    zIndex: 10,
  },
  schoolIconForFromSchool: {
    position: "absolute",
    top: 0,
    left: "-9px",
    "& svg": {
      width: "18px",
      height: "18px",
    },
  },
  dropOffIcon: {
    position: "absolute",
    top: 0,
    left: "-8px",
    "& svg": {
      width: "18px",
      height: "18px",
    },
  },
  locationWrapper: {
    display: "flex",
    justifyContent: "flex-start",
    gap: 2,
    position: "relative",
    height: "90px",
  },
  addressWrapper: {
    padding: "0 20px",
    marginBottom: "30px",
  },
  etaText: {
    padding: "2px 4px",
    fontSize: "10px",
    fontWeight: 700,
    backgroundColor: "#FFFFFF",
    color: "#3C3C3C",
    textAlign: "center",
    width: "max-content",
    display: "block",
    boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.25)",
    borderRadius: "4px",
    position: "absolute",
    bottom: "calc(100% + 5px)",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
  },
}));

const RouteDetail: React.FC<RouteDetailProps> = ({
  selectedRoute,
  assignmentVehicleLocations,
  handleCloseAssignmentDetail,
  setSelectedAssignment,
}) => {
  const classes = useStyles();

  const [assignmentDetail, setAssignmentDetail] = useState<IAssignment | null>(
    null,
  );
  const [drivers, setDrivers] = useState<any[]>([]);
  const [busAssistants, setBusAssistants] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [busProgress, setBusProgress] = useState<number>(0);
  const [etaProgress, setEtaProgress] = useState<number>(0);
  const [isBusIconHidden, setIsBusIconHidden] = useState<boolean>(false);

  const provider = useDataProvider();
  const notify = useNotify();

  const formattedAssignment = (data: IAssignment) => {
    let locationsToUse = data.locations;

    if (data.routeType === "to_school") {
      locationsToUse = locationsToUse.slice(0, -1);
    } else if (data.routeType === "from_school" || data.routeType === "cca") {
      locationsToUse = locationsToUse.slice(1);
    }

    return {
      ...data,
      locations: locationsToUse,
    };
  };

  const renderSchoolNameText = (name: string) => {
    if (name.length > 24) {
      return (
        <Tooltip title={name}>
          <span>{`${name.substring(0, 24)}...`}</span>
        </Tooltip>
      );
    } else {
      return name;
    }
  };

  const renderAddressText = (address: string) => {
    if (address.length > 56) {
      return (
        <Tooltip title={address}>
          <span>{`${address.substring(0, 56)}...`}</span>
        </Tooltip>
      );
    } else {
      return address;
    }
  };

  const resetData = () => {
    setAssignmentDetail(null);
    setBusProgress(0);
    setEtaProgress(0);
    setIsBusIconHidden(false);
  };

  useEffect(() => {
    if (selectedRoute) {
      resetData();

      provider
        .getOne("assignments", {
          id: selectedRoute.id,
        })
        .then((res) => {
          setAssignmentDetail(formattedAssignment(res.data as IAssignment));
          setSelectedAssignment(formattedAssignment(res.data as IAssignment));

          if (res.data.driverId) {
            getDriversByIds(provider, [res.data.driverId]).then((result) => {
              setDrivers(result);
            });
          }

          if (res.data.busAssistantId) {
            getBusAssistantsByIds(provider, [res.data.busAssistantId]).then(
              (result) => {
                setBusAssistants(result);
              },
            );
          }

          if (res.data.vehicleId) {
            getVehiclesByIds(provider, [res.data.vehicleId]).then((result) => {
              setVehicles(result);
            });
          }
        })
        .catch((error) => {
          notify("Error fetching assignment:", error);
        });
    }
  }, [selectedRoute]);

  useEffect(() => {
    if (assignmentDetail) {
      const currentBusLocation = assignmentVehicleLocations.find(
        (assignment) => assignment.id === assignmentDetail.id,
      );

      const { progress, etaInMinutes, isHidden } = calculateBusPosition(
        assignmentDetail,
        currentBusLocation?.vehicle,
        currentBusLocation?.nextStop,
      );

      setBusProgress(progress);
      setEtaProgress(etaInMinutes);
      setIsBusIconHidden(isHidden);
    }
  }, [assignmentDetail, assignmentVehicleLocations]);

  return (
    <Grid
      container
      spacing={2}
      className={`${classes.routeDetails} ${
        selectedRoute ? classes.visibleRouteDetails : ""
      }`}
    >
      <Grid item xs={12}>
        <IconButton
          size="small"
          onClick={() => {
            handleCloseAssignmentDetail();
            setAssignmentDetail(null);
          }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </Grid>

      {assignmentDetail ? (
        <>
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                style={{ color: "#2B3674", marginBottom: 0 }}
              >
                {assignmentDetail.routeName}
              </Typography>
              <Typography variant="subtitle2" style={{ marginBottom: "10px" }}>
                {assignmentDetail.routeType === "to_school"
                  ? "HOME TO SCHOOL TRIP"
                  : assignmentDetail.routeType === "from_school"
                    ? "SCHOOL TO HOME TRIP"
                    : "CCA TRIP"}
              </Typography>

              <Typography
                variant="body2"
                style={{ color: "#2B3674", marginBottom: 0 }}
              >
                Driver{" "}
                {drivers.length > 0
                  ? `${drivers[0].fullName} (${drivers[0].contactNumber})`
                  : "-"}
              </Typography>
              <Typography
                variant="body2"
                style={{ color: "#2B3674", marginBottom: 0 }}
              >
                Bus Assistant{" "}
                {busAssistants.length > 0
                  ? `${busAssistants[0].fullName} (${busAssistants[0].contactNumber})`
                  : "-"}
              </Typography>
              <Typography
                variant="body2"
                style={{ color: "#2B3674", marginBottom: 0 }}
              >
                Vehicle {vehicles.length > 0 ? vehicles[0].plateNumber : "-"}
              </Typography>
            </Box>

            <></>
          </Grid>

          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-around"
              divider
            >
              <Typography
                variant="body2"
                className={classes.assignmentTime}
                gutterBottom
              >
                STARTED {moment(assignmentDetail.startedAt).format("hh:mm A")}
              </Typography>
              <Typography
                variant="body2"
                className={classes.assignmentTime}
                gutterBottom
              >
                ESTIMATED{" "}
                {moment(assignmentDetail.estimatedEndedAt).format("hh:mm A")}
              </Typography>
            </Stack>
          </Grid>

          <Box
            style={{
              padding: "5px 20px",
              maxHeight: "calc(70vh - 350px)",
              overflowY: "auto",
            }}
          >
            <Grid item xs={12} style={{ marginTop: "20px" }}>
              {assignmentDetail.routeType === "from_school" ||
              assignmentDetail.routeType === "cca" ? (
                <Box
                  className={classes.locationWrapper}
                  style={{
                    borderLeft: "3px dashed #ccc",
                  }}
                >
                  {isBusIconHidden ? (
                    <></>
                  ) : (
                    <Box
                      className={classes.busIcon}
                      style={{
                        top: `${busProgress}%`,
                      }}
                    >
                      <span className={classes.etaText}>
                        {etaProgress === 0
                          ? "Arrived"
                          : etaProgress > 1
                            ? `${etaProgress}mins`
                            : `${etaProgress}min`}
                      </span>
                      <BusIcon />
                    </Box>
                  )}

                  <DropOffIcon className={classes.schoolIconForFromSchool} />

                  <Box className={classes.addressWrapper}>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      {renderSchoolNameText(assignmentDetail.school.name)}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      {renderAddressText(assignmentDetail.school.address)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <></>
              )}

              {assignmentDetail.locations.map((location, index) => {
                const allPassengersHaveNotTaking =
                  location.passengers.length > 0 &&
                  location.passengers.every((p) =>
                    p.logs.some(
                      (log) =>
                        log.type === "not_taking" ||
                        log.type === "exclude_student",
                    ),
                  );

                return (
                  <Box
                    className={classes.locationWrapper}
                    style={{
                      borderLeft:
                        (assignmentDetail.routeType === "from_school" ||
                          assignmentDetail.routeType === "cca") &&
                        index === assignmentDetail.locations.length - 1
                          ? "3px dashed transparent"
                          : "3px dashed #ccc",
                    }}
                  >
                    {assignmentDetail.routeType === "to_school" &&
                    index === 0 &&
                    !isBusIconHidden ? (
                      <Box
                        className={classes.busIcon}
                        style={{
                          top: `${busProgress}%`,
                        }}
                      >
                        <span className={classes.etaText}>
                          {etaProgress === 0
                            ? "Arrived"
                            : etaProgress > 1
                              ? `${etaProgress}mins`
                              : `${etaProgress}min`}
                        </span>
                        <BusIcon />
                      </Box>
                    ) : (
                      <></>
                    )}

                    <Box
                      className={
                        assignmentDetail.routeType === "to_school" &&
                        index === 0
                          ? classes.iconForFirstLocation
                          : classes.icon
                      }
                    >
                      <MapLocationDotIcon
                        width={25}
                        height={25}
                        colorOne={
                          allPassengersHaveNotTaking ? "#F87171" : "#78B9FF"
                        }
                        colorTwo={
                          allPassengersHaveNotTaking ? "#EF4444" : "#127EF1"
                        }
                      />

                      {/* Number overlay */}
                      <span
                        style={{
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "12px",
                          position: "absolute",
                          top: "45%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      >
                        {assignmentDetail.routeType === "to_school"
                          ? location.order
                          : location.order - 1}
                      </span>
                    </Box>

                    <Box className={classes.addressWrapper}>
                      <Typography variant="body2">
                        {renderAddressText(location.address)}
                      </Typography>
                      <Tooltip
                        title={location.passengers
                          .map(
                            (p) =>
                              `${p.student.firstName} ${p.student.lastName}`,
                          )
                          .join(", ")}
                      >
                        <Typography
                          variant="body2"
                          style={{ fontWeight: "bold", cursor: "pointer" }}
                        >{`${location.passengers.length} STUDENTS ${allPassengersHaveNotTaking ? "(NOT TAKING)" : ""}`}</Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}

              {assignmentDetail.routeType === "to_school" && (
                <Box className={classes.locationWrapper}>
                  <DropOffIcon className={classes.dropOffIcon} />
                  <Box className={classes.addressWrapper}>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      {renderSchoolNameText(assignmentDetail.school.name)}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      {renderAddressText(assignmentDetail.school.address)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>
          </Box>
        </>
      ) : (
        <Grid item xs={12} style={{ textAlign: "center", marginTop: "20px" }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: "10px" }}>
            Loading...
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default RouteDetail;
